import {type} from 'arktype'
import Big from 'big.js'

import type {AccountInformation} from '@/auth/model'
import {isReadableError} from '@/lib/api/ReadableError'
import {responseData} from '@/lib/api/ResponseData'
import throwIfError from '@/lib/api/throwIfError'
import {localeUnderscoreToHyphen, type SupportedLocales} from '@/lib/locale/locale'
import {PaginatedRewardResponse} from '@/models/rewardModel'
import call from '@/utils/api/call'
import UnexpectedError from '@/utils/api/UnexpectedError'

const NON_ERROR_API_CODES = ['IAA0000', 'ICA0000']

export async function sendReferral({
  emails,
  signal,
  locale,
}: {
  emails: string[]
  signal?: AbortSignal
  locale: SupportedLocales
}) {
  return call
    .post(
      '/api/auth/v1/referrals/send-email',
      {
        emails,
      },
      {
        signal,
        headers: {
          'Accept-Language': localeUnderscoreToHyphen(locale),
        },
      },
    )
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
    })
    .catch(error => {
      if (isReadableError(error)) throw error
      throw new UnexpectedError(error)
    })
}

export async function getRewards({
  signal,
  locale,
}: {
  signal?: AbortSignal
  locale: SupportedLocales
}) {
  return call
    .get('/api/checkout/v1/wallets/history?pageSize=100', {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = PaginatedRewardResponse(res.data)
      return data.data.records
    })
    .catch(error => {
      throw new UnexpectedError(error)
    })
}

export async function getReferrer({signal}: {signal?: AbortSignal}): Promise<boolean> {
  return call
    .get('/api/checkout/v1/referrals/check-referred', {signal})
    .then(res => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- its safe
      const data = res.data.data as unknown
      if (typeof data !== 'object' || data === null || !('referred' in data)) {
        throw new Error('Invalid response data')
      }
      return !!data.referred
    })
    .catch(() => {
      return false
    })
}

export async function updateReferrer({
  referrer,
  account,
}: {
  referrer: string
  account: AccountInformation | undefined
}) {
  if (!account) throw new Error('No account information')

  const accountName = account.accountName

  return call
    .post('/api/checkout/v1/referrals', {
      refCode: referrer.split('/').pop(),
      accountName,
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
    })
}

export const Discount = type({
  discount: type('string.numeric.parse').or(type.number),
})
export type Discount = typeof Discount.infer
export const DiscountResponse = responseData(Discount)
export type DiscountResponse = typeof DiscountResponse.infer

export async function getDiscount({
  price,
  accountId,
}: {
  price: Big
  accountId: AccountInformation['id'] | undefined
}) {
  if (!accountId) return Promise.resolve({discount: 0})

  return call.get(`/api/checkout/v1/wallets/discount?price=${price}`).then(res => {
    throwIfError(res, NON_ERROR_API_CODES)
    const data = DiscountResponse(res.data)
    return data.data
  })
}
